import achievements from "../services/achievement";
import organization from "../services/organization";
import notification from "../services/notification";
import _ from "lodash";

const defaultErrorMessage = "Oops, something went wrong.";

export default {
    namespaced: true,

    state: {
        loading: false,
        view: null,
        list: [],
        page: 1,
        prevPage: null,
        nextPage: null,
        maxPage: null,
        message: null,
        errors: null,

        detail: {
            milestone: null,
            points: null,
            point_expiry: null,
            point_validity: null,
            description: null,
            published: null,
            logo_image: null,
            target_segment_uuid: null,
            options: null,
            achievement_unlock_config_id: null,
            csv: null
        },

        membership_activation: false,
        manual_seeding: false,
        seeding_file: null,
        seeding_base: null,

        points_multiplier: {
            status: false,
            branches: [],
            multiplier_settings: []
        },
        welcomeDiscountDetails: null,
        pagination: {
            currentPage: 1,
            prevPage: 0,
            nextPage: 0,
            maxPage: 0,
            totalPerPage: 1,
            currentFirstItem: 1,
            currentLastItem: 1,
            initialPageCount: 0,
            perPage: 10
        },
    },

    mutations: {
        setList(state, response) {
            state.list = response;
            state.loading = false;
        },

        pagination(state, pagination) {
            state.maxPage = pagination.max_page;
            state.prevPage = pagination.prev_page;
            state.nextPage = pagination.next_page;
            state.currentPage = state.prevPage + 1;

            state.pagination.totalPerPage = pagination.list.length;
            state.pagination.maxPage =
                state.pagination.totalPerPage > 0 ? pagination.max_page : 0;
            state.pagination.prevPage = pagination.prev_page;
            state.pagination.nextPage = pagination.next_page;
            state.pagination.currentPage = state.pagination.prevPage + 1;
            state.pagination.currentFirstItem =
                state.pagination.totalPerPage > 0
                    ? state.pagination.initialPageCount *
                            state.pagination.prevPage +
                        1
                    : 0;
            state.pagination.currentLastItem =
                state.pagination.currentFirstItem > 0
                    ? state.pagination.currentFirstItem +
                        state.pagination.totalPerPage -
                        1
                    : 0;
        },

        setDetail(state, detail) {
            state.detail.milestone = detail.badgeTitle;
            state.detail.points = parseFloat(detail.points);

            state.detail.point_expiry = 0;
            if (detail.pointsExpiry) {
                state.detail.point_expiry = 1;
            }

            state.detail.point_validity = detail.selectedValidity;
            state.detail.description = detail.milestoneDescription;
            state.detail.published = detail.appVisibility;

            state.detail.target_segment_uuid = detail.selectedTargetSegment;
            state.detail.uuid = detail.uuid;
            state.detail.achievement_unlock_config_id = detail.uuid;

            if (detail.logoImage != null && detail.logoImage != "") {
                if (detail.logoImage.search("data:image") == -1) {
                    state.detail.logo_image = null;
                } else {
                    state.detail.logo_image = detail.logoImage.replace(
                        /data:image\/png;base64,/gi,
                        ""
                    );
                }
            } else {
                state.detail.logo_image = null;
            }

            state.detail.options = JSON.stringify({
                logo: state.detail.logo_image,
                points: parseFloat(detail.points)
            });

            if (state.membership_activation) {
                state.detail.options = JSON.stringify({
                    logo: state.detail.logo_image,
                    start_date: detail.start_date,
                    end_date: detail.end_date
                });
            }

            if (state.manual_seeding) {
                state.detail.csv = state.seeding_base;
            }

            if (state.points_multiplier.status) {
                state.detail.options = JSON.stringify({
                    logo: state.detail.logo_image,
                    multiplier_settings:
                        state.points_multiplier.multiplier_settings
                });
                state.detail["multiplier_settings"] =
                    state.points_multiplier.multiplier_settings;
            }
        },

        loading(state, value = true) {
            state.loading = value;
        },

        errors(state, errors) {
            if (errors != null) {
                state.errors = errors.errors;
            } else {
                state.errors = errors;
            }
            state.loading = false;
        },
        setErrors(state, errors) {
            state.errors = errors;
        },
        setMembershipActivation(state, status) {
            state.membership_activation = status;
        },

        setImportFile(state, file) {
            state.seeding_file = file;
        },

        setSeedingDetail(state, base64) {
            state.seeding_base = base64;
        },

        clearSeedingFile(state) {
            state.seeding_file = null;
            state.seeding_base = null;
            state.detail.csv = null;
        },

        setManualSeeding(state, status) {
            state.manual_seeding = status;
        },

        setPointsMultiplier(state, status) {
            state.points_multiplier.status = status;
        },

        setBranches(state, branches) {
            state.points_multiplier.branches = branches;
        },

        setMultiplierDetail(state, multiplier_settings) {
            state.points_multiplier.multiplier_settings = multiplier_settings;
        },
        setWelcomeDiscountDetails(state, value) {
            state.welcomeDiscountDetails = value;
        },
        setInitialPageCount(state, list) {
            state.pagination.initialPageCount = list.length;
        }
    },

    getters: {
        outlineAchievements: state => {
            let publishedAchievements = _.filter(state.list, o => {
                return o.published == 1;
            });
            return _.chunk(publishedAchievements);
        }
    },

    actions: {
        async getList({ commit }, param) {
            commit("loading");
            try {
                let response = await achievements.getList(param);
                
                if (param === 1) {
                    await commit("setInitialPageCount", response.list);
                }

                response.list[0]["tooltip"] =
                    "Users earn points for completing their profile.";
                response.list[1]["tooltip"] =
                    "Users earn points every time they share loyalty program to a friend. Note that only referrals with complete profiles are eligible for points.";
                response.list[3]["tooltip"] =
                    "Points a user earns are doubled until merchants switch this off.";
                response.list[4]["tooltip"] =
                    "Seeding or awarding of points manually to your customers.";

                commit("setList", response.list);
                commit("pagination", response);
                commit("loading", false);
            } catch (errors) {
                commit("errors", errors);
                commit("loading", false);
            }
        },

        async getTargetSegmentOptions({ commit }) {
            commit("loading");
            try {
                let response = await achievements.getTargetSegmentOptions();
                commit("loading", false);
                return response;
            } catch (errors) {
                commit("errors", errors);
                commit("loading", false);
            }
        },

        updateAchievement: async ({ commit, state }) => {
            commit("loading");
            try {
                let response = await achievements.updateAchievement(
                    state.detail
                );
                commit("loading", false);
                return response;
            } catch (errors) {
                commit("errors", errors);
                commit("loading", false);
            }
        },

        readImportFile: async ({ commit, state }) => {
            if (state.seeding_file != null) {
                let reader = new FileReader();
                reader.readAsDataURL(state.seeding_file);

                reader.onload = e => {
                    commit(
                        "setSeedingDetail",
                        e.target.result.split("base64,").pop()
                    );
                };
            } else {
                commit("setSeedingDetail", null);
            }
        },

        getBranches: async ({ commit }) => {
            commit("loading", true);
            try {
                let response = await organization.getBranches();

                let branches = response.map(branch => {
                    return {
                        value: branch.uuid,
                        text: branch.name
                    };
                });
                commit("setBranches", branches);
                commit("loading", false);
            } catch (errors) {
                commit("errors", errors);
                commit("loading", false);
            }
        },
        async getWelcomeDiscountDetails({ commit }) {
            commit("loading", true);
            commit("errors", null);

            try {
                const { data } = await achievements.getWelcomeDiscountDetails();
                commit("setWelcomeDiscountDetails", data);
            } catch (errors) {
                commit("errors", errors);
                notification.warning(defaultErrorMessage);
            } finally {
                commit("loading", false);
            }
        },
        async saveWelcomeDiscountDetails({ commit }, payload) {
            commit("loading", true);
            commit("errors", null);

            try {
                const {
                    message
                } = await achievements.saveWelcomeDiscountDetails(payload);
                notification.success(message);
            } catch (errors) {
                commit("errors", errors);
                notification.warning(defaultErrorMessage);
            } finally {
                commit("loading", false);
            }
        }
    }
};
